import { Article } from "types/article";
import { TimeSlot } from "types/time-slot";

/**
 * Checks if a time slot conflicts with existing booked articles.
 *
 * Returns the conflicting article with a reason:
 * - "collision": The time slot's start time falls within an already booked article's time range
 * - "timeConstraint": The time slot's duration would extend into a following article
 *
 * @param timeSlot - The time slot to check for conflicts
 * @param articles - Array of already booked articles
 * @param webRuleId - The rule ID of the activity being edited (to filter out self-conflicts)
 * @param ignoredRuleIds - Optional set of rule IDs to exclude from conflict detection,
 *   used for multi-activity experiences so sibling activities don't conflict with each other
 * @returns The conflicting article with conflict reason, or null if no conflict
 *
 * @example
 * // Collision example
 * const result = getConflictingArticle(
 *   { start: moment('18:15'), end: moment('20:15') },
 *   [{ start: moment('18:00'), end: moment('19:00'), ruleId: 2 }],
 *   1
 * );
 * // Returns { ...article, conflictReason: "collision" }
 *
 * @example
 * // Time constraint example
 * const result = getConflictingArticle(
 *   { start: moment('19:00'), end: moment('21:00') },
 *   [{ start: moment('20:00'), end: moment('22:30'), ruleId: 2 }],
 *   1
 * );
 * // Returns { ...article, conflictReason: "timeConstraint" }
 */
function getConflictingArticle(
    timeSlot: TimeSlot,
    articles: Article[],
    webRuleId: number,
    ignoredRuleIds?: Set<number>
): (Article & { conflictReason: "collision" | "timeConstraint" }) | null {
    if (!articles || articles.length === 0) return null;

    const sortedArticles = articles
        .filter((a) => a.ruleId !== webRuleId && !ignoredRuleIds?.has(a.ruleId))
        .sort((a1, a2) => (a1.start.isBefore(a2.start) ? -1 : 1));

    // Check for time constraint: does the timeslot's duration extend into the next article?
    const followingArticle = sortedArticles.find((article) => {
        return timeSlot.start.isBefore(article.start);
    });

    const isTimeConstrained = followingArticle && timeSlot.end.isAfter(followingArticle.start);

    // Check for collision: does the timeslot's start time fall within an existing article?
    const overlappingArticle = sortedArticles.find((article) => {
        return timeSlot.start.isSameOrAfter(article.start) && timeSlot.start.isBefore(article.end);
    });

    const isCollisionDetected = !!overlappingArticle;

    // Collision takes precedence over time constraint
    if (isTimeConstrained && !isCollisionDetected && followingArticle) {
        return { ...followingArticle, conflictReason: "timeConstraint" };
    } else if (isCollisionDetected && overlappingArticle) {
        return { ...overlappingArticle, conflictReason: "collision" };
    }

    return null;
}

export { getConflictingArticle };
